import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Plus, Trash2, ArrowLeft, Save, FileText, Upload, RefreshCw, Download, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import MarkdownEditor from "../../components/MarkdownEditor";

const newQuestion = () => ({
  text: "",
  type: "single",
  options: ["", ""],
  correct: [],
  block: "",
});

const fmtSize = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function AdminTestEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [passPercentage, setPassPercentage] = useState(70);
  const [timeLimit, setTimeLimit] = useState(0);
  const [kind, setKind] = useState("normal");
  const [studyHours, setStudyHours] = useState("");
  const [price, setPrice] = useState("");
  const [moreInfo, setMoreInfo] = useState("");
  const [area, setArea] = useState("");
  const [questions, setQuestions] = useState([newQuestion()]);
  const [saving, setSaving] = useState(false);
  const [theoryFiles, setTheoryFiles] = useState([]);
  const [theoryBusy, setTheoryBusy] = useState(false);
  const [headerImage, setHeaderImage] = useState(null); // {ext, mime, size_bytes, version, original_name}
  const [headerBusy, setHeaderBusy] = useState(false);
  const [existingAreas, setExistingAreas] = useState([]);

  useEffect(() => {
    // Fetch unique areas from all existing tests, for the datalist autocomplete
    api.get("/admin/tests").then(({ data }) => {
      const set = new Set();
      (data || []).forEach((t) => {
        if (t.area && t.area.trim()) set.add(t.area.trim());
      });
      setExistingAreas([...set].sort());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/admin/tests/${id}`).then(({ data }) => {
      setTitle(data.title);
      setDescription(data.description || "");
      setPassPercentage(data.pass_percentage);
      setTimeLimit(data.time_limit_minutes || 0);
      setKind(data.kind || "normal");
      setStudyHours(data.study_hours ?? "");
      setPrice(data.price ?? "");
      setMoreInfo(data.more_info || "");
      setArea(data.area || "");
      setQuestions(
        data.questions.map((q) => ({
          text: q.text,
          type: q.type,
          options: q.options,
          correct: q.correct || [],
          block: q.block || "",
        }))
      );
      setTheoryFiles(data.theory_files || []);
      setHeaderImage(data.header_image || null);
    });
  }, [id]);

  const updateQ = (idx, patch) => {
    setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };

  const changeType = (idx, type) => {
    const q = questions[idx];
    let options = q.options;
    let correct = [];
    if (type === "truefalse") {
      options = ["Verdadero", "Falso"];
    } else if (q.type === "truefalse") {
      options = ["", ""];
    }
    updateQ(idx, { type, options, correct });
  };

  const toggleCorrect = (idx, optIdx, multi) => {
    const q = questions[idx];
    let next;
    if (multi) {
      next = q.correct.includes(optIdx)
        ? q.correct.filter((x) => x !== optIdx)
        : [...q.correct, optIdx];
    } else {
      next = [optIdx];
    }
    updateQ(idx, { correct: next });
  };

  const updateOption = (idx, optIdx, value) => {
    const opts = [...questions[idx].options];
    opts[optIdx] = value;
    updateQ(idx, { options: opts });
  };

  const addOption = (idx) => {
    const opts = [...questions[idx].options, ""];
    updateQ(idx, { options: opts });
  };

  const removeOption = (idx, optIdx) => {
    const q = questions[idx];
    const opts = q.options.filter((_, i) => i !== optIdx);
    const correct = q.correct.filter((c) => c !== optIdx).map((c) => (c > optIdx ? c - 1 : c));
    updateQ(idx, { options: opts, correct });
  };

  const save = async () => {
    if (!title.trim()) return toast.error("El título es obligatorio");
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!q.text.trim()) return toast.error(`Pregunta ${i + 1}: falta el texto`);
      if (q.options.some((o) => !o.trim()))
        return toast.error(`Pregunta ${i + 1}: opciones vacías`);
      if (q.correct.length === 0)
        return toast.error(`Pregunta ${i + 1}: marca al menos una respuesta correcta`);
    }
    setSaving(true);
    try {
      const payload = {
        title,
        description,
        pass_percentage: Number(passPercentage),
        time_limit_minutes: Number(timeLimit) || 0,
        kind,
        study_hours: studyHours === "" ? null : Number(studyHours),
        price: price === "" ? null : Number(price),
        more_info: moreInfo,
        area: area.trim() || null,
        questions,
      };
      if (id) await api.put(`/admin/tests/${id}`, payload);
      else await api.post("/admin/tests", payload);
      toast.success("Guardado");
      navigate("/admin/tests");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // ---- Theory PDFs ----
  const refreshTheory = async () => {
    if (!id) return;
    const { data } = await api.get(`/admin/tests/${id}`);
    setTheoryFiles(data.theory_files || []);
  };

  const uploadTheory = async (file, replaceFileId = null) => {
    if (!id) {
      toast.error("Guarda el test antes de añadir material");
      return;
    }
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Solo PDF");
      return;
    }
    setTheoryBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = replaceFileId
        ? `/admin/tests/${id}/theory/${replaceFileId}`
        : `/admin/tests/${id}/theory`;
      const method = replaceFileId ? "put" : "post";
      await api[method](url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(replaceFileId ? "PDF actualizado" : "PDF añadido");
      await refreshTheory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir PDF");
    } finally {
      setTheoryBusy(false);
    }
  };

  const deleteTheory = async (fileId) => {
    if (!window.confirm("¿Eliminar este PDF? Los estudiantes ya no podrán descargarlo.")) return;
    setTheoryBusy(true);
    try {
      await api.delete(`/admin/tests/${id}/theory/${fileId}`);
      toast.success("PDF eliminado");
      await refreshTheory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setTheoryBusy(false);
    }
  };

  const downloadTheoryAdmin = async (fileId, name) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/tests/${id}/theory/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo descargar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "material.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ---- Header image ----
  const headerPreviewUrl = headerImage
    ? `${API}/public/tests/${id}/header?v=${headerImage.version || headerImage.uploaded_at || ""}`
    : null;

  const uploadHeader = async (file) => {
    if (!id) {
      toast.error("Guarda el test antes de añadir imagen");
      return;
    }
    if (!file) return;
    const okTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Formato no soportado. Usa PNG, JPG o WEBP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagen máx. 5 MB");
      return;
    }
    setHeaderBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/tests/${id}/header`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeaderImage(data);
      toast.success("Imagen guardada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir imagen");
    } finally {
      setHeaderBusy(false);
    }
  };

  const deleteHeader = async () => {
    if (!window.confirm("¿Eliminar la imagen de cabecera?")) return;
    setHeaderBusy(true);
    try {
      await api.delete(`/admin/tests/${id}/header`);
      setHeaderImage(null);
      toast.success("Imagen eliminada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setHeaderBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="test-editor-page">
      <Button variant="ghost" onClick={() => navigate("/admin/tests")} className="text-gray-600">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver
      </Button>

      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-gray-900">
          {id ? "Editar curso" : "Nuevo curso"}
        </h1>
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="save-test">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4">
        <div>
          <Label htmlFor="title">Título</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="test-title-input" />
        </div>
        <div>
          <Label htmlFor="desc">Descripción</Label>
          <Textarea id="desc" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="area">Área formativa</Label>
          <Input
            id="area"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="p. ej. Sanidad, Restauración, Servicios Sociales…"
            list="area-suggestions"
            data-testid="test-area-input"
          />
          <datalist id="area-suggestions">
            {(existingAreas || []).map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
          <p className="text-xs text-gray-500 mt-1">
            En el catálogo público los cursos se agrupan por área. Deja en blanco para mostrar en &quot;Otros&quot;.
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="w-80">
            <Label htmlFor="kind">Tipo de prueba</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger id="kind" data-testid="test-kind-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">TALLER — Entrega de certificado</SelectItem>
                <SelectItem value="final">CURSO — Entrega de diploma</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {kind === "final"
                ? "Al aprobar, el alumno recibirá un Diploma (con los ajustes del panel Diploma)."
                : "Al aprobar, el alumno recibirá un Certificado de aprovechamiento."}
            </p>
          </div>
          <div className="w-44">
            <Label htmlFor="pp">% para aprobar</Label>
            <Input
              id="pp"
              type="number"
              min={0}
              max={100}
              value={passPercentage}
              onChange={(e) => setPassPercentage(e.target.value)}
              data-testid="pass-percentage-input"
            />
          </div>
          <div className="w-44">
            <Label htmlFor="tl">Tiempo límite (min)</Label>
            <Input
              id="tl"
              type="number"
              min={0}
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
              placeholder="0 = sin límite"
              data-testid="time-limit-input"
            />
          </div>
          <div className="w-44">
            <Label htmlFor="sh">Horas lectivas</Label>
            <Input
              id="sh"
              type="number"
              min={0}
              step="0.5"
              value={studyHours}
              onChange={(e) => setStudyHours(e.target.value)}
              placeholder="Ej: 40"
              data-testid="study-hours-input"
            />
            <p className="text-xs text-gray-500 mt-1">Aparecerán en el PDF.</p>
          </div>
          <div className="w-44">
            <Label htmlFor="price">Precio (€)</Label>
            <Input
              id="price"
              type="number"
              min={0}
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Ej: 49,90"
              data-testid="price-input"
            />
            <p className="text-xs text-gray-500 mt-1">Opcional. Vacío = sin precio.</p>
          </div>
        </div>
      </Card>

      {/* "Más info" block (shown in the home modal) */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-3" data-testid="more-info-card">
        <div>
          <Label className="text-sm">Más info (catálogo público)</Label>
          <p className="text-xs text-gray-500 mt-1">
            Este texto se muestra en el botón &quot;Más info&quot; del catálogo de la home pública. Admite formato:
            negrita, cursiva, listas, encabezados y enlaces.
          </p>
        </div>
        <MarkdownEditor
          value={moreInfo}
          onChange={setMoreInfo}
          rows={10}
          placeholder="Describe el curso: a quién va dirigido, temario, requisitos, qué incluye…"
          testId="more-info-editor"
        />
      </Card>

      {/* Header image (shown in catalog card + Más info modal) */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4" data-testid="header-image-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-brand" />
              <h2 className="font-display text-lg text-gray-900">Imagen de cabecera</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-md">
              Aparecerá en la tarjeta del catálogo público y en el modal &quot;Más info&quot;. Recomendado:
              proporción 16:9, máx. 5 MB, PNG/JPG/WEBP.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="header-upload"
              type="file"
              accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadHeader(f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!id || headerBusy}
              onClick={() => document.getElementById("header-upload").click()}
              data-testid="upload-header-image"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {headerImage ? "Reemplazar" : "Subir imagen"}
            </Button>
            {headerImage && (
              <Button
                variant="ghost"
                size="sm"
                disabled={headerBusy}
                onClick={deleteHeader}
                title="Eliminar imagen"
                data-testid="delete-header-image"
              >
                <X className="w-3.5 h-3.5 text-red-500" />
              </Button>
            )}
          </div>
        </div>

        {!id ? (
          <p className="text-xs text-gray-500 italic">
            Guarda el test primero para poder añadir una imagen.
          </p>
        ) : headerImage ? (
          <div
            className="aspect-[16/9] w-full max-w-md rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
            data-testid="header-image-preview"
          >
            <img
              src={headerPreviewUrl}
              alt={title || "Cabecera del curso"}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[16/9] w-full max-w-md rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400 bg-gray-50">
            <div className="text-center px-4">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">Sin imagen. Sube una para destacar el curso en el catálogo.</p>
            </div>
          </div>
        )}
      </Card>

      {/* Theory PDFs */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4" data-testid="theory-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand" />
              <h2 className="font-display text-lg text-gray-900">Material de estudio (PDFs)</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Cada estudiante asignado podrá descargar estos PDFs hasta que apruebe el curso. Máximo 20 MB por archivo.
            </p>
          </div>
          <div>
            <input
              id="theory-upload-new"
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadTheory(f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!id || theoryBusy}
              onClick={() => document.getElementById("theory-upload-new").click()}
              data-testid="add-theory-pdf"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              Añadir PDF
            </Button>
          </div>
        </div>

        {!id ? (
          <p className="text-xs text-gray-500 italic">
            Guarda el test primero para poder añadir material de estudio.
          </p>
        ) : theoryFiles.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="no-theory">
            Aún no hay archivos. Añade un PDF para que los estudiantes puedan estudiar antes del test.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-md">
            {theoryFiles.map((f) => (
              <li
                key={f.file_id}
                className="flex items-center justify-between gap-3 px-3 py-2.5 flex-wrap"
                data-testid={`theory-row-${f.file_id}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 truncate">{f.original_name}</span>
                  <span className="text-[11px] text-gray-500 font-mono-data flex-shrink-0">
                    {fmtSize(f.size_bytes)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => downloadTheoryAdmin(f.file_id, f.original_name)}
                    title="Descargar"
                    data-testid={`theory-download-${f.file_id}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  <input
                    id={`theory-replace-${f.file_id}`}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadTheory(file, f.file_id);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={theoryBusy}
                    onClick={() => document.getElementById(`theory-replace-${f.file_id}`).click()}
                    title="Actualizar (reemplazar archivo)"
                    data-testid={`theory-update-${f.file_id}`}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={theoryBusy}
                    onClick={() => deleteTheory(f.file_id)}
                    title="Eliminar"
                    data-testid={`theory-delete-${f.file_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="space-y-4">
        {questions.map((q, idx) => {
          const prevBlock = idx > 0 ? (questions[idx - 1].block || "").trim() : null;
          const thisBlock = (q.block || "").trim();
          const showDivider = idx === 0 || thisBlock !== prevBlock;
          return (
            <div key={idx}>
              {showDivider && (
                <div className="flex items-center gap-3 mt-6 mb-2" data-testid={`block-divider-${idx}`}>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#bcdfd9] to-transparent" />
                  <span className="text-[11px] tracking-[0.2em] uppercase text-brand-ink/70 font-mono-data px-2">
                    {thisBlock ? thisBlock : "Sin bloque"}
                  </span>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#bcdfd9] to-transparent" />
                </div>
              )}
              <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <span className="text-sm font-medium text-gray-700 pt-2">Pregunta {idx + 1}</span>
                  <div className="flex items-end gap-2">
                    <div className="w-56">
                      <Label htmlFor={`q-block-${idx}`} className="text-xs">Bloque</Label>
                      <Input
                        id={`q-block-${idx}`}
                        value={q.block}
                        onChange={(e) => updateQ(idx, { block: e.target.value })}
                        placeholder="Ej: Conceptos básicos"
                        data-testid={`q-block-${idx}`}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setQuestions((qs) => qs.filter((_, i) => i !== idx))}
                      disabled={questions.length === 1}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Texto</Label>
                  <Textarea
                    value={q.text}
                    onChange={(e) => updateQ(idx, { text: e.target.value })}
                    data-testid={`q-text-${idx}`}
                  />
                </div>
                <div className="w-60">
                  <Label>Tipo</Label>
                  <Select value={q.type} onValueChange={(v) => changeType(idx, v)}>
                    <SelectTrigger data-testid={`q-type-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Opción única</SelectItem>
                      <SelectItem value="multiple">Opciones múltiples</SelectItem>
                      <SelectItem value="truefalse">Verdadero / Falso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Opciones (marca la/las correctas)</Label>
                  {q.type === "single" ? (
                    <RadioGroup
                      value={q.correct[0]?.toString() ?? ""}
                      onValueChange={(v) => toggleCorrect(idx, Number(v), false)}
                    >
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <RadioGroupItem value={oi.toString()} id={`r-${idx}-${oi}`} />
                          <Input
                            value={opt}
                            onChange={(e) => updateOption(idx, oi, e.target.value)}
                            className="flex-1"
                          />
                          {q.type !== "truefalse" && q.options.length > 2 && (
                            <Button variant="ghost" size="sm" onClick={() => removeOption(idx, oi)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </RadioGroup>
                  ) : (
                    q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <Checkbox
                          checked={q.correct.includes(oi)}
                          onCheckedChange={() => toggleCorrect(idx, oi, true)}
                        />
                        <Input
                          value={opt}
                          onChange={(e) => updateOption(idx, oi, e.target.value)}
                          className="flex-1"
                          disabled={q.type === "truefalse"}
                        />
                        {q.type !== "truefalse" && q.options.length > 2 && (
                          <Button variant="ghost" size="sm" onClick={() => removeOption(idx, oi)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                  {q.type !== "truefalse" && (
                    <Button variant="outline" size="sm" onClick={() => addOption(idx)}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Añadir opción
                    </Button>
                  )}
                </div>
              </Card>
            </div>
          );
        })}
      </div>

      <Button
        variant="outline"
        onClick={() =>
          setQuestions((qs) => {
            const lastBlock = qs.length ? qs[qs.length - 1].block || "" : "";
            return [...qs, { ...newQuestion(), block: lastBlock }];
          })
        }
        data-testid="add-question"
      >
        <Plus className="w-4 h-4 mr-2" /> Añadir pregunta
      </Button>
    </div>
  );
}
